import './styles.scss';

import { ComponentProps, useRef } from 'react';
import TableRow, { TableCollapse } from '@/common/Table/TableRow';

import Portal from '@/components/Portal';
import TableCell from '@/common/Table/TableCell';
import TableControls from '@/common/Table/TableControls';
import { forwardRef } from 'react';
import { getClasses } from '@/utils';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';

export type TableProps = {
  header?: JSX.Element;
  footer?: JSX.Element;
  showControls?: boolean;
  controls?: JSX.Element;
  onStart?: () => void;
  onEnd?: () => void;
} & Omit<ComponentProps<'table'>, 'onSelect'>;

type TableComponent = React.ForwardRefExoticComponent<TableProps & React.RefAttributes<HTMLTableElement>> & {
  Row: typeof TableRow;
  Cell: typeof TableCell;
  Collapse: typeof TableCollapse;
};

const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ header, footer, showControls = false, controls, className, children, onStart, onEnd, ...props }, outerRef): JSX.Element => {
    const innerRef = useRef<HTMLTableElement>(null);
    const startRef = useRef<HTMLTableRowElement>(null);
    const endRef = useRef<HTMLTableRowElement>(null);
    const classes = getClasses('Table', className);
    const withControls = showControls !== undefined && showControls !== false;
    const addonsElement = innerRef.current?.querySelector?.('.Table-Addons');

    useIntersectionObserver(
      ([{ isIntersecting }]) => {
        if (isIntersecting) {
          onStart?.();
        }
      },
      [startRef]
    );
    useIntersectionObserver(
      ([{ isIntersecting }]) => {
        if (isIntersecting) {
          onEnd?.();
        }
      },
      [endRef]
    );

    return (
      <div className={classes} ref={innerRef}>
        <table {...props} ref={outerRef}>
          {header && <thead className="Table-Head">{header}</thead>}
          <tbody className="Table-Body">
            <tr className="Table-Start" ref={startRef} />
            {children}
            <tr className="Table-End" ref={endRef} />
          </tbody>
          {footer && <tfoot className="Table-Foot">{footer}</tfoot>}
        </table>
        {withControls && (
          <>
            <TableControls getTable={() => innerRef.current} />
            {controls && addonsElement && <Portal container={addonsElement}>{controls}</Portal>}
          </>
        )}
      </div>
    );
  }
) as TableComponent;

Table.displayName = 'Table';
Table.Row = TableRow;
Table.Collapse = TableCollapse;
Table.Cell = TableCell;
export default Table;
